import { useEffect, useState } from 'react'
import {
  Users as UsersIcon, Package as PackageIcon, Receipt as ReceiptIcon,
  CircleDollarSign, type LucideIcon,
} from 'lucide-react'
import { api, errorMessage, type AdminEvent } from '@/lib/api'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Sparkline } from '@/components/Sparkline'
import { fmtRelative } from '@/lib/format'
import { useI18n, type TKey } from '@/lib/i18n'

type Stats = Awaited<ReturnType<typeof api.stats>>
type Series = Awaited<ReturnType<typeof api.timeseries>>

export function StatsScreen() {
  const { t } = useI18n()
  const [stats, setStats] = useState<Stats | null>(null)
  const [series, setSeries] = useState<Series | null>(null)
  const [events, setEvents] = useState<AdminEvent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([api.stats(), api.timeseries(14), api.listEvents({ limit: 12 })])
      .then(([s, t, e]) => { setStats(s); setSeries(t); setEvents(e.events) })
      .catch((e) => setError(errorMessage(e)))
  }, [])

  if (error) return <PageError msg={error}/>
  if (!stats || !series) return <PageLoading/>

  // Defensive: if the /api proxy ever mis-deploys we may receive HTML or a
  // partial JSON object. Coerce every field we read into a known shape so a
  // malformed response degrades to "all zeros" instead of crashing the SPA.
  const orderTotals = (stats.orders && typeof stats.orders === 'object')
    ? stats.orders
    : { pending: 0, paid: 0, fulfilled: 0, refunded: 0, cancelled: 0 } as Record<string, number>
  const totalOrders = Object.values(orderTotals).reduce((a, b) => a + Number(b ?? 0), 0)
  const seriesPoints = Array.isArray(series.series) ? series.series : []
  const orderSeries   = seriesPoints.map((p) => Number(p?.count ?? 0))
  // sparkline 用跨币种总和（数值含义混乱但趋势 ok）
  const revenueSeries = seriesPoints.map((p) => Number(p?.paidCents ?? 0) / 100)
  // 14 天已支付：分币种汇总，UI 渲染 ¥xxx/$xxx
  const paidCny14 = seriesPoints.reduce((a, p) => a + Number(p?.paidCentsCny ?? 0), 0)
  const paidUsd14 = seriesPoints.reduce((a, p) => a + Number(p?.paidCentsUsd ?? 0), 0)
  const fmtAmount = (cents: number) => (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const revenue14Display =
    paidCny14 === 0 && paidUsd14 === 0
      ? '¥0.00 / $0.00'
      : `¥${fmtAmount(paidCny14)} / $${fmtAmount(paidUsd14)}`

  return (
    <div className="p-6 space-y-5">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">{t('stats.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('stats.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard title={t('stats.kpi.users')}           value={stats.users.total.toLocaleString()}
                 Icon={UsersIcon}/>
        <KpiCard title={t('stats.kpi.activeProducts')}  value={stats.products.active.toLocaleString()}
                 Icon={PackageIcon}/>
        <KpiCard title={t('stats.kpi.orders14')}        value={orderSeries.reduce((a, b) => a + b, 0).toLocaleString()}
                 Icon={ReceiptIcon}
                 spark={orderSeries}/>
        <KpiCard title={t('stats.kpi.revenue14')}       value={revenue14Display}
                 Icon={CircleDollarSign}
                 spark={revenueSeries}
                 sparkColor="text-success"/>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>{t('stats.orders.title')}</CardTitle>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              <StatusChip statusKey="status.pending"   count={orderTotals.pending}   tone="warning" />
              <StatusChip statusKey="status.paid"      count={orderTotals.paid}      tone="info" />
              <StatusChip statusKey="status.fulfilled" count={orderTotals.fulfilled} tone="success" />
              <StatusChip statusKey="status.refunded"  count={orderTotals.refunded}  tone="default" />
              <StatusChip statusKey="status.cancelled" count={orderTotals.cancelled} tone="default" />
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              {t('stats.orders.totalGmv', {
                total: totalOrders.toLocaleString(),
                gmv: (stats.revenue.grossLifetimeCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 }),
              })}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('stats.recent.title')}</CardTitle></CardHeader>
          <CardBody className="px-0 pb-0">
            <ActivityFeed events={events}/>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

function KpiCard({ title, value, Icon, spark, sparkColor }: {
  title: string
  value: string
  Icon: LucideIcon
  spark?: number[]
  sparkColor?: string
}) {
  return (
    <Card className="relative overflow-hidden">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Icon size={16} strokeWidth={1.75} className="text-muted-foreground/60"/>
      </CardHeader>
      <CardBody>
        <div className="text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
      </CardBody>
      {spark && spark.length > 0 && (
        <div className={`absolute bottom-0 right-0 left-0 ${sparkColor ?? 'text-primary'} opacity-90`}>
          <Sparkline values={spark} width={300} height={36} ariaLabel={`${title} trend`}/>
        </div>
      )}
    </Card>
  )
}

function StatusChip({ statusKey, count, tone }: {
  statusKey: TKey; count: number; tone: 'warning'|'info'|'success'|'default'
}) {
  const { t } = useI18n()
  return (
    <div className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5">
      <Badge tone={tone}>{t(statusKey)}</Badge>
      <span className="text-sm font-medium tabular-nums">{Number(count ?? 0).toLocaleString()}</span>
    </div>
  )
}

function ActivityFeed({ events }: { events: AdminEvent[] | null }) {
  const { t } = useI18n()
  if (!events) return <div className="px-4 py-6 text-sm text-muted-foreground">{t('app.loading')}</div>
  if (events.length === 0) {
    return <div className="px-4 py-6 text-sm text-muted-foreground">{t('stats.recent.empty')}</div>
  }
  return (
    <ul className="divide-y divide-border">
      {events.slice(0, 10).map((e) => (
        <li key={e.id} className="flex items-start gap-2 px-4 py-2.5">
          <EventDot type={e.type}/>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{describeEvent(e, t)}</div>
            <div className="text-xs text-muted-foreground">
              {fmtRelative(e.createdAt)}
              {e.payload?.actor ? <> · <span className="font-mono">{e.payload.actor}</span></> : null}
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

function EventDot({ type }: { type: string }) {
  const tone =
    type.startsWith('admin.')   ? 'bg-primary' :
    type.startsWith('order.')   ? 'bg-info' :
    type.startsWith('referral.')? 'bg-success' :
    'bg-muted-foreground'
  return <span className={`mt-1.5 inline-block h-1.5 w-1.5 rounded-full ${tone}`} aria-hidden/>
}

type T = ReturnType<typeof useI18n>['t']

function describeEvent(e: AdminEvent, t: T): string {
  const p = e.payload?.data ?? e.payload ?? {}
  switch (e.type) {
    case 'admin.order.update':       return t('stats.event.orderUpdate', { id: shortId(p.id), to: p.to ?? '?' })
    case 'admin.product.create':     return t('stats.event.productCreate', { sku: p.sku ?? shortId(p.id) })
    case 'admin.product.update':     return t('stats.event.productUpdate', { id: p.id ? shortId(p.id) : '?' })
    case 'admin.product.deactivate': return t('stats.event.productDeactivate', { id: shortId(p.id) })
    case 'admin.user.update':        return t('stats.event.userUpdate', { id: shortId(p.id) })
    case 'admin.orders.export':      return t('stats.event.ordersExport', { rows: p.rowCount })
    case 'admin.users.export':       return t('stats.event.usersExport', { rows: p.rowCount })
    case 'order.create':             return t('stats.event.orderPlaced', { id: shortId(p.orderId), amount: (p.amountCents / 100).toFixed(2) })
    case 'referral.award':           return t('stats.event.referralAward', { amount: (p.rewardCents / 100).toFixed(2), id: shortId(e.userId ?? '') })
    default:                         return e.type
  }
}

function shortId(id: string): string {
  return id ? id.slice(-8) : '?'
}

export function PageLoading() {
  return <div className="p-8 text-sm text-muted-foreground">Loading…</div>
}

export function PageError({ msg }: { msg: string }) {
  return (
    <div className="p-8">
      <div className="rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-danger">
        {msg}
      </div>
    </div>
  )
}
